import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { resourceState, shouldRetryMarketing, toMarketingError, type ResourceState } from '@/features/marketing-shared';
import { fetchSmsCampaign, smsMarketingKeys } from '../api/sms-marketing-api';
import { useSmsMarketing } from './context';
import type { SmsCampaign } from './types';

export const SMS_RECIPIENTS_PAGE_SIZE = 50;

/** Detalhe da campanha de SMS. A linha da lista desenha o cabeçalho enquanto o detalhe chega. */
export function useSmsCampaignDetail(
  id: string | undefined,
  page: number,
): { campaign: SmsCampaign | null; state: ResourceState; detailPending: boolean } {
  const { idPassbook, campaigns, campaignsState, scenario } = useSmsMarketing();
  const query = useQuery({
    queryKey: smsMarketingKeys.campaign(idPassbook, id ?? '-', page),
    queryFn: () => fetchSmsCampaign(id!, idPassbook, { page, pageSize: SMS_RECIPIENTS_PAGE_SIZE }),
    enabled: Boolean(id),
    retry: shouldRetryMarketing,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });

  const fromList = campaigns.find((item) => item.id === id) ?? null;
  if (scenario === 'carregando') return { campaign: null, state: { ...campaignsState, phase: 'loading' }, detailPending: true };
  if (scenario === 'erro') return { campaign: null, state: campaignsState, detailPending: false };
  if (scenario === 'vazio') return { campaign: null, state: { ...campaignsState, phase: 'ready' }, detailPending: false };

  const detail = resourceState(query, toMarketingError);
  if (query.data) return { campaign: query.data, state: detail, detailPending: query.isPlaceholderData };
  if (detail.phase === 'error' && detail.error?.kind !== 'not_found' && fromList) {
    return { campaign: fromList, state: detail, detailPending: false };
  }
  if (detail.phase === 'loading' && fromList) return { campaign: fromList, state: { ...detail, phase: 'ready' }, detailPending: true };
  if (detail.phase === 'loading' && campaignsState.phase === 'loading') return { campaign: null, state: detail, detailPending: true };
  return { campaign: null, state: detail, detailPending: false };
}
